import { useState, useEffect } from 'react'
import { supabase } from '@/lib/supabase'
import { MetricWeek } from './useMetricWeeks'

const TAG = '[useWeeklyPlan]'

/**
 * Данные недельного плана из studio_weekly_plan_vs_fact
 */
export interface WeeklyPlanData {
  weekly_gross_revenue_plan?: number
  weekly_new_sales_amount_plan?: number
  weekly_renewals_amount_plan?: number
  weekly_returning_clients_amount_plan?: number
  weekly_upgrades_amount_plan?: number
  weekly_leads_plan?: number
  weekly_appointments_plan?: number
  weekly_new_visitors_plan?: number
  weekly_new_sales_plan?: number
  weekly_new_subscriptions_count_plan?: number
  weekly_refunds_amount_plan?: number
}

/**
 * Hook для загрузки недельного плана из studio_weekly_plan_vs_fact.
 * Использует week_id из metric_weeks для получения конкретного плана недели.
 */
export function useWeeklyPlan(studioId: number, currentWeek: MetricWeek | null) {
  const [weeklyPlan, setWeeklyPlan] = useState<WeeklyPlanData | null>(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    const loadWeeklyPlan = async () => {
      if (!currentWeek || currentWeek.week_id < 0) {
        // Если week_id отрицательный - это вычисленная неделя (fallback), нет плана в БД
        console.log(TAG, '⏭️ Skip: нет currentWeek или week_id < 0 (computed week)')
        setWeeklyPlan(null)
        return
      }

      setLoading(true)
      console.log(TAG, '📅 Загрузка недельного плана:', {
        studioId,
        weekId: currentWeek.week_id,
        weekNumber: currentWeek.week_number_in_period,
        weekRange: `${currentWeek.week_start}..${currentWeek.week_end}`
      })

      try {
        const { data, error } = await supabase
          .from('studio_weekly_plan_vs_fact')
          .select(`
            weekly_gross_revenue_plan,
            weekly_new_sales_amount_plan,
            weekly_renewals_amount_plan,
            weekly_returning_clients_amount_plan,
            weekly_upgrades_amount_plan,
            weekly_leads_plan,
            weekly_appointments_plan,
            weekly_new_visitors_plan,
            weekly_new_sales_plan,
            weekly_new_subscriptions_count_plan,
            weekly_refunds_amount_plan
          `)
          .eq('studio_internal_id', studioId)
          .eq('week_id', currentWeek.week_id)
          .maybeSingle<any>()

        if (error) {
          console.error(TAG, '❌ Ошибка загрузки недельного плана:', error.message)
          setWeeklyPlan(null)
          return
        }

        console.log(TAG, '📦 Недельный план загружен:', {
          found: !!data,
          gross_revenue_plan: data?.weekly_gross_revenue_plan ?? null
        })

        setWeeklyPlan(
          data
            ? {
                ...data,
                weekly_new_sales_plan: (data.weekly_new_sales_plan || 0) + (data.weekly_new_subscriptions_count_plan || 0)
              }
            : null
        )
      } catch (error) {
        console.error(TAG, '💥 Exception:', error)
        setWeeklyPlan(null)
      } finally {
        setLoading(false)
      }
    }

    loadWeeklyPlan()
  }, [studioId, currentWeek?.week_id])

  return { weeklyPlan, loading }
}
